import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ControlProps, optionIs, rankWith } from "@jsonforms/core";
import { withJsonFormsControlProps } from "@jsonforms/react";
import { ChevronsUpDown, User, X } from "lucide-react";
import React, { useMemo, useState } from "react";

import { useHubSpotOwner, useHubSpotOwners } from "@/api/integrations/hubspot";
import { useIntegrationConnection } from "@/hooks/useIntegrationConnection";
import type { HubspotContactEntity } from "@cohesive/api-entities";
import { getEntityId, Spinner } from "@cohesive/ui-components";

interface HubSpotOwnerControlProps extends ControlProps {
  data: HubspotContactEntity | string | undefined;
}

const HubSpotOwnerControl: React.FC<HubSpotOwnerControlProps> = ({
  data,
  enabled,
  handleChange,
  path,
  label,
}) => {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  // Extract ID from data (handles both string ID and entity object)
  // Handle both string IDs and entity objects
  const selectedId =
    typeof data === "string" ? data : data ? getEntityId(data as any) : null;

  const integration = useIntegrationConnection("hubspot");

  const { data: ownerData } = useHubSpotOwner(
    {
      integrationConnectionId: integration?.id || "",
      id: selectedId || "",
    },
    {
      enabled: Boolean(integration && selectedId),
    },
  );

  const { data: owners = [], isLoading: loading } = useHubSpotOwners(
    {
      integrationConnectionId: integration?.id || "",
    },
    {
      enabled: Boolean(integration),
    },
  );

  const selectLabel = useMemo(() => {
    if (!ownerData) {
      return `Select Owner...`;
    }

    // ownerData is now a HubspotContactEntity with data.name
    return ownerData.data.name || `Select Owner...`;
  }, [ownerData]);

  // Owners are returned as HubspotContactEntity[] from the toolbox
  const mappedData: HubspotContactEntity[] = owners;

  return (
    <div>
      {label && <label className="text-sm font-medium">{label}</label>}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            disabled={!enabled}
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-full justify-between"
          >
            {ownerData ? (
              <div className="flex items-center gap-2 overflow-hidden">
                <div className="flex items-center justify-center w-7 h-7 shrink-0 rounded-full bg-muted">
                  <User className="w-4! h-4!" />
                </div>
                <span className="truncate">{selectLabel}</span>
              </div>
            ) : (
              <span className="text-muted-foreground">Select owner...</span>
            )}
            <div className="flex items-center gap-1 ml-2 shrink-0">
              {ownerData && (
                <div
                  role="button"
                  aria-label="Clear selection"
                  className="cursor-pointer"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleChange(path, null);
                  }}
                >
                  <X className="h-4 w-4 opacity-50 hover:opacity-100" />
                </div>
              )}
              <ChevronsUpDown className="h-4 w-4 opacity-50" />
            </div>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[400px] p-0" align="start">
          <Command shouldFilter={false}>
            <CommandInput
              placeholder={`Search owners...`}
              value={searchQuery}
              onValueChange={setSearchQuery}
            />
            <CommandList>
              {loading ? (
                <div className="flex items-center justify-center p-4">
                  <Spinner size="sm" />
                </div>
              ) : (
                <>
                  <CommandEmpty>No owners found.</CommandEmpty>
                  <CommandGroup>
                    {mappedData.map((entity) => {
                      const entityData = entity.data;

                      if (!entityData) {
                        return null;
                      }

                      return (
                        <CommandItem
                          key={entityData.id}
                          value={[entityData.name, entityData.id].join(" ")}
                          onSelect={() => {
                            // Return full entity object
                            handleChange(path, entity);
                            setOpen(false);
                          }}
                        >
                          <div className="flex items-center justify-center w-7 h-7 shrink-0 rounded-full bg-muted">
                            <User className="w-4! h-4!" />
                          </div>
                          <span className="truncate">{entityData.name}</span>
                        </CommandItem>
                      );
                    })}
                  </CommandGroup>
                </>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
};

export default withJsonFormsControlProps(HubSpotOwnerControl);

export const hubSpotOwnerTester = rankWith(
  5,
  optionIs("control", "hubspot_owner"),
);
